/**
 * Mainnet priority fee estimation via Helius getPriorityFeeEstimate.
 *
 * Relocated from frontend/src/lib/tx-engine/priority-fee.ts as part of the
 * auth boundary migration (spec 009 FILE 14 Group E). Logger is injected.
 * `fetch` is universal. Platform-neutral — safe in src/auth/ core.
 *
 * Devnet uses its fixed floor because Helius does not expose this method there.
 * Mainnet estimates are requested WITHOUT a transaction, so they are a
 * network-global recommendation (not scoped to the writable accounts in any
 * one tx). This lets `PriorityFeeCache` serve one value across all submissions
 * for its refresh window — the fee is best-effort and a coarse global figure
 * is sufficient for landing.
 */

import type { Connection } from '@solana/web3.js';

import type { TxEngineLogger } from '../config.js';
import { isValidTransactionTraceId } from '../diagnostics/lifecycle.js';

const FLOOR_DEVNET = 1_000;
const FLOOR_MAINNET = 10_000;

interface HeliusPriorityFeeResponse {
  result?: {
    priorityFeeEstimate?: number;
  };
}

export function priorityFeeFloor(network: string): number {
  return network === 'mainnet' ? FLOOR_MAINNET : FLOOR_DEVNET;
}

/**
 * Resolve microlamports per compute unit for transaction submission.
 * Devnet uses its fixed floor without an RPC request. Other networks request a
 * network-global Helius estimate and use the floor when it returns zero/missing.
 * Transport and parse failures are re-thrown for the cache/pipeline fallback.
 */
export async function fetchPriorityFeeEstimate(
  connection: Connection,
  network: string,
  logger: TxEngineLogger,
  traceId?: string,
): Promise<number> {
  const floor = priorityFeeFloor(network);

  if (network === 'devnet') {
    return floor;
  }

  const priorityLevel = network === 'mainnet' ? 'High' : 'Medium';
  const endpoint = typeof connection.rpcEndpoint === 'string' ? connection.rpcEndpoint : '';

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 'priority-fee',
      method: 'getPriorityFeeEstimate',
      params: [{ options: { priorityLevel } }],
    }),
    signal: AbortSignal.timeout(5_000),
  });

  const data = (await response.json()) as HeliusPriorityFeeResponse;
  const estimate = data.result?.priorityFeeEstimate;

  if (typeof estimate === 'number' && estimate > 0) {
    return estimate;
  }

  logger.warn('tx.priority_fee.estimate_missing_or_zero', {
    network,
    floor: String(floor),
    ...(isValidTransactionTraceId(traceId) ? { traceId } : {}),
  });
  return floor;
}
